/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  ListItemText,
  SxProps,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { showSnackbar } from '@services/snackbar';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import React, { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { helperTextRender } from '../utils/helpers';
import StandardTextfield from './standard-textfield';

interface SearchAutocompleteProps {
  disabled?: boolean;
  id?: string;
  label?: string;
  updateSelectedKey: (obj: { value: string | string[] }) => void;
  selectedKey: string | string[];
  apiTrigger: (search: string, seed: number) => Promise<any>;
  getName: (obj: any) => any;
  initialText: string;
  sx: SxProps;
  error: boolean;
  other: any;
  size?: any;
  showAddOther: boolean;
  topLabel?: string;
  required?: boolean;
  optional?: boolean;
  helperText?: React.ReactNode;
  others?: any;
  allowMultiple?: boolean;
  maxLimit?: number;
  helperTextIcon?: string;
}

export default function SearchAutocomplete(props: SearchAutocompleteProps) {
  const {
    disabled,
    id,
    label,
    updateSelectedKey,
    selectedKey = '',
    apiTrigger,
    getName,
    initialText = '',
    sx = {},
    error = false,
    other = { name: 'Other', value: 'Other' },
    size = 'medium',
    showAddOther,
    topLabel,
    required,
    optional,
    helperText,
    allowMultiple = false,
    maxLimit = 0,
    helperTextIcon,
    others
  } = props;
  const theme = useTheme();
  const listboxRef: React.Ref<Element> = useRef(null);
  const listRef = useRef(null);
  const myRef = useRef(null);
  const [searchText, setSearchText] = React.useState(allowMultiple ? '' : (selectedKey as string));
  const [open, setOpen] = React.useState(false);
  const [valueObj, setValueObj] = React.useState<any>(
    allowMultiple ? selectedKey : { name: selectedKey, value: selectedKey }
  );
  const [options, setOptions]: any[] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [seed, setSeed] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [showTextfield, setShowTextfield] = useState(false);

  // keep internal value in sync when parent changes selectedKey
  useEffect(() => {
    if (!allowMultiple) {
      setSearchText(selectedKey as string);
      setValueObj({ name: selectedKey, value: selectedKey });
    } else {
      setValueObj(selectedKey);
    }
  }, [selectedKey]);

  useEffect(() => listboxRef.current?.scrollIntoView());

  const debouncedSearch = useDebouncedCallback((str = searchText, ...rest) => {
    callApi(str, ...rest);
  }, 700);

  const callApi = async (
    searchThis: string,
    newSeedValue: number = 0,
    actualOptions = options,
    ref: any = listRef?.current
  ) => {
    setLoading(true);
    if (ref) {
      // ref.scrollTop -= 20;
    }
    const response: any = await apiTrigger(searchThis, newSeedValue);
    let lastScrollHeight = 0;
    if (ref) {
      lastScrollHeight = ref.scrollHeight;
    }

    setOptions([...actualOptions, ...(response?.list ?? [])]);
    if (ref) {
      setTimeout(() => {
        ref.scrollTop = lastScrollHeight;
      }, 100);
    }
    setTotal(response.total);
    setSeed(newSeedValue);
    setLoading(false);
  };
  return (
    <>
      {topLabel && optional ? (
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '.5rem' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: '.5rem' }}>
            {topLabel}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: '.5rem', color: 'grey' }}>
            {'(Optional)'}
          </Typography>
        </Box>
      ) : (
        topLabel && (
          <Typography variant="subtitle2" sx={{ mb: '.5rem', fontWeight: '600' }}>
            {topLabel}
            {required && <span style={{ color: 'red' }}> *</span>}
          </Typography>
        )
      )}
      {showTextfield ? (
        <>
          <StandardTextfield
            disabled={disabled}
            error={error}
            topLabel={label}
            size={size ?? 'medium'}
            onChange={(e) => updateSelectedKey({ value: e.target.value })}
            sx={{ width: '100%' }}
            placeholder={others?.placeholder || 'Enter value'}
          />
          {!disabled && showAddOther && (
            <span style={{ fontWeight: '400', color: '#666666' }}>
              <Button
                sx={{
                  typography: 'body2',
                  color: theme.palette.primary.main,
                  m: 0,
                  mt: '.4rem',
                  p: '.2rem .5rem',
                  borderRadius: '2px',
                  width: 'fit-content',
                  fontWeight: '500'
                }}
                onClick={() => {
                  updateSelectedKey({ value: '' });
                  setShowTextfield(false);
                }}>
                Select from options
              </Button>
            </span>
          )}
        </>
      ) : (
        <Autocomplete
          id={id}
          ref={myRef}
          size={size ?? 'medium'}
          disabled={disabled}
          sx={{
            width: '100%',
            // 'zIndex': 999999999,
            margin: '0',
            '& .MuiInput-root': {
              color: 'currentColor',
              fontFamily: 'Source Sans 3',
              fontSize: '1.32rem'
            },
            '& .MuiInputLabel-root': {
              // color: 'rgb(0 0 0 / 44%)',
              fontFamily: 'Source Sans 3',
              fontSize: '1.4rem'
            },
            '& .MuiInputBase-input': {
              color: 'currentColor',
              fontFamily: 'Source Sans 3',
              fontSize: '1.32rem'
            },
            '& .MuiChip-label': {
              color: 'currentColor',
              fontFamily: 'Source Sans 3',
              fontSize: '1.32rem',
              maxWidth: '200px',
              whiteSpace: 'break-spaces'
            },
            '& .MuiChip-root': { height: 'unset', maxWidth: '100%' }
          }}
          onBlur={() => {
            setOpen(false);
            setSearchText('');
          }}
          multiple={allowMultiple}
          selectOnFocus
          open={open}
          value={allowMultiple ? (Array.isArray(valueObj) ? valueObj : []) : valueObj}
          onOpen={() => {
            if (maxLimit > 0 && valueObj?.length >= maxLimit) {
              showSnackbar(`You can only select up to ${maxLimit} options.`, 'error');
              return null;
            }
            setSearchText('');
            setOptions([]);
            setTotal(0);
            setSeed(0);
            setLoading(true);
            if (!disabled && !showTextfield) {
              debouncedSearch('', 0, []);
            }
            setOpen(true);
          }}
          onChange={(option: any, value: any) => {
            if (allowMultiple) {
              updateSelectedKey(value);
            } else {
              if (!value && searchText?.length > 0) {
                setOptions([]);
                setSearchText('');
                debouncedSearch('', 0, []);
                setValueObj({ name: '', value: '' });
              } else {
                updateSelectedKey({ value: value?.value });
                setValueObj(value);
              }
            }

            setOpen(false);
            // handleClose();
            return option?.target?.id === value?.value;
          }}
          getOptionLabel={(option) => getName(option).primary}
          filterOptions={(x) => x}
          options={
            allowMultiple && Array.isArray(valueObj)
              ? options.filter(
                  (option: any) => !valueObj.some((v: any) => v?.value === option.value)
                )
              : options
          }
          loading={loading && open}
          loadingText={<p style={{ fontSize: '1.3rem' }}> Loading...</p>}
          noOptionsText={<p style={{ fontSize: '1.3rem' }}> No options</p>}
          ListboxProps={{
            sx: {
              //   minHeight: 200,
              maxHeight: '45vh',
              textAlign: 'left',
              '& .MuiAutocomplete-popupIndicatorOpen': {
                // 'zIndex': '99999 !important',
                textAlign: 'left'
              },
              scrollBehavior: 'smooth'
            },
            onScroll: async (event) => {
              const listboxNode = event.currentTarget;
              if (
                listboxNode.scrollTop + listboxNode.clientHeight >=
                listboxNode.scrollHeight - 5
              ) {
                // listboxNode.scrollTop -= 50;
                if (options?.length < total) {
                  debouncedSearch(searchText, seed + 50, undefined, listboxNode);
                }
              }
            },
            ref: listboxRef
          }}
          renderOption={(props, option, { inputValue, index }) => {
            let matches;
            let parts;
            const name = getName(option);
            if (name.primary) {
              matches = match(name.primary, inputValue, { insideWords: true });
              parts = parse(name.primary, matches);
            }

            return (
              <>
                <ListItemText
                  {...(props as any)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    // "alignItems": "center",
                    overflowWrap: 'anywhere',
                    textAlign: 'left'
                  }}
                  id={name.primary}
                  primary={parts?.map((part: any, index: number) => (
                    <span
                      key={`${part.text}-${index}`}
                      style={{
                        fontWeight: part.highlight ? 700 : 400,
                        textAlign: 'left',
                        fontSize: '1.3rem'
                      }}>
                      {part.text}
                    </span>
                  ))}
                  secondary={name.secondary}
                />
                {index === options?.length - 1 && loading && (
                  <div
                    style={{
                      position: 'sticky',
                      bottom: '0',
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%',
                      backgroundColor: 'white',
                      padding: '.7rem 0'
                    }}>
                    <CircularProgress style={{ height: '3rem', width: '3rem' }} />
                  </div>
                )}
              </>
            );
          }}
          renderInput={(params) => (
            <Tooltip title={searchText !== '' ? searchText : initialText} arrow placement="bottom">
              <TextField
                // autoFocus
                {...params}
                required
                style={{ width: 'webkit-fill-available' }}
                // placeholder={searchText !== '' ? searchText : ''}
                label={label}
                variant="outlined"
                // value={searchText}
                inputProps={{
                  ...params.inputProps
                  //   style: {
                  //     ...styles.inputProps.autocomplete(size)
                  //   }
                }}
                onChange={(e) => {
                  // setValueObj({ name: '', value: e.target.value });
                  setSearchText(e.target.value);
                  debouncedSearch(e.target.value, 0, [], listRef?.current);
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && open ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                  sx: { marginBottom: '2px' }
                }}
                error={error}
                helperText={
                  <>
                    {typeof helperText === 'string' ? (
                      <Box sx={{ ml: '-11px' }}>
                        <Typography
                          variant="caption"
                          sx={{ ml: 0 }}
                          color={error ? 'error' : 'text.secondary'}>
                          <Grid container alignItems="center" columnGap={0.5}>
                            {helperTextRender(helperTextIcon)}
                            {helperText}
                          </Grid>
                        </Typography>
                      </Box>
                    ) : helperText ? (
                      helperText
                    ) : null}
                    {!disabled && showAddOther && (
                      <Box sx={{ display: 'flex', alignItems: 'center', ml: '-11px' }}>
                        <Typography variant="body2" sx={{ color: '#666666' }}>
                          Unable to find {label}?{' '}
                        </Typography>
                        <Button
                          sx={{
                            typography: 'body2',
                            color: 'primary.main',
                            cursor: 'pointer',
                            p: '.2rem .5rem',
                            borderRadius: '2px',
                            width: 'fit-content',
                            fontWeight: '500'
                          }}
                          onClick={() => {
                            setShowTextfield(true);
                            updateSelectedKey({ value: '' });
                          }}>
                          Enter manually
                        </Button>
                      </Box>
                    )}
                  </>
                }
                FormHelperTextProps={{
                  sx: {
                    marginTop: 0
                  }
                }}
              />
            </Tooltip>
          )}
        />
      )}
    </>
  );
}
